<template>
	<div class="fade">
		<nuxt-link :to="articleHref" class="article-teaser">
			<div class="mb-4 aspect-[420/254] rounded overflow-hidden">
				<nuxt-img
					v-if="articleContent.image"
					:src="`${articleContent.image}`"
					provider="storyblok"
					loading="lazy"
					format="webp"
					width="800"
					height="600"
					class="max-w-full object-cover duration-200 ease-linear transition-all hover:scale-[1.2]"
					alt=""
				/>
			</div>
		</nuxt-link>
		<div class="text-xs">
			<nuxt-link
				v-for="category in categs"
				:key="category.uuid"
				class="inline pr-3 font-medium"
				:to="categoryHref(category.slug)"
			>
				{{ category.content.category_name }}
			</nuxt-link>
		</div>
		<ArticlePublicationDates
			:updated="articleContent.updated_at"
			:first-published="articleContent.first_published_at"
			:fallback-date="articleContent.date"
			class="text-xs text-gray-super-dark mb-2"
		/>
		<nuxt-link :to="articleHref">
			<h2 class="padding-normal mb-2 lg:text-xl text-lg font-medium">
				{{ articleContent.name }}
			</h2>
			<p class="leading-relaxed line-clamp-2">
				{{ articleContent.intro }}
			</p>
		</nuxt-link>
	</div>
</template>

<script setup lang="ts">
import type { BlokArticleContent } from "~/types/storyblok/storyblok";
import { normalizeStoryblokSlug } from "~/utils/storyblok/resolveStoryblokSlug";

const localePath = useLocalePath();
const { stripFolder } = useBlogPath();

interface Props {
	articleContent: BlokArticleContent;
	articleLink: string;
	articleCategories: {
		uuid: string;
		slug: string;
		content: { category_name: string };
	}[];
}
const { articleContent, articleCategories, articleLink } = defineProps<Props>();

// `useBlogPath.stripFolder` removes whichever site folder is in the
// slug — active or fallback. Articles served from the default site
// (for locales with `isLocaleBlog: false`) carry the fallback folder
// prefix, not the active locale's, so the teaser must strip both.
const articleSlug = computed(() =>
	stripFolder(normalizeStoryblokSlug(articleLink))
);
const blogRoot = computed(() => articleSlug.value.split("/")[0] ?? "");
const articleCategorySlug = computed(
	() => articleSlug.value.split("/")[1] ?? ""
);

const articleHref = computed(() => localePath(`/${articleSlug.value}`));
const categoryHref = (categorySlug: string) =>
	localePath(`/${blogRoot.value}/${categorySlug}`);

const categs = ref<Props["articleCategories"]>([]);
const filterCategories = () => {
	const categorySlug = articleCategorySlug.value;
	if (!categorySlug) return [];
	return articleCategories.filter((category) => category.slug === categorySlug);
};
categs.value = filterCategories();
</script>
